// Locale registry. Mostly European. Each locale gets an hreflang
// alternate on every page (via lib/seo.ts) and a switcher entry in
// the footer.
//
// Stage 1 (this PR): declare the locales, set hreflang correctly,
// expose a footer switcher. Content remains in English for now ,
// the routing layer is wired so per-locale variants can ship
// progressively without a full architectural rewrite.

export type Locale = {
  code: string        // ISO 639-1 (+ optional region), used in hreflang
  label: string       // Display name in the user's own language
  flag: string        // Emoji flag for the switcher
  href: string        // Where the locale renders. '/' means default English.
  default?: boolean
  /** True if a per-locale build of the marketing site is live. When
   *  false the switcher renders the row as "Coming soon" and no link
   *  is followed (the path also redirects to '/' via next.config). */
  ready?: boolean
}

export const locales: Locale[] = [
  // Cookie-based switching today, `href: '/'` for every ready locale.
  // When per-locale path-prefix routing ships (next-intl proper) these
  // hrefs become '/es', '/fr', etc.
  { code: 'en',    label: 'English',    flag: '🇬🇧', href: '/',    default: true, ready: true },
  { code: 'es',    label: 'Español',    flag: '🇪🇸', href: '/',    ready: true },
  { code: 'fr',    label: 'Français',   flag: '🇫🇷', href: '/',    ready: true },
  { code: 'de',    label: 'Deutsch',    flag: '🇩🇪', href: '/',    ready: true },
  { code: 'it',    label: 'Italiano',   flag: '🇮🇹', href: '/it' },
  { code: 'nl',    label: 'Nederlands', flag: '🇳🇱', href: '/nl' },
  { code: 'pl',    label: 'Polski',     flag: '🇵🇱', href: '/pl' },
  { code: 'pt',    label: 'Português',  flag: '🇵🇹', href: '/pt' },
  { code: 'sv',    label: 'Svenska',    flag: '🇸🇪', href: '/sv' },
  { code: 'da',    label: 'Dansk',      flag: '🇩🇰', href: '/da' },
]

export const defaultLocale = locales.find((l) => l.default) || locales[0]

/** Locale codes that aren't yet ready, used by next.config redirects
 *  and the language switcher to keep the UX honest. */
export const placeholderLocaleCodes = locales.filter((l) => !l.ready && !l.default).map((l) => l.code)

export function altHreflangs(path: string): { hreflang: string; href: string }[] {
  // Returns the alternate-language URL set for a given canonical path.
  // Google requires every hreflang language to map to a DISTINCT URL.
  // Cookie-switched locales (href: '/') serve the same URL as English,
  // so declaring them produced 4-5 hreflang entries all pointing at the
  // identical URL on every page — Ahrefs flagged all 1,042 crawled pages
  // with "page referenced for more than one language in hreflang".
  // Only path-prefixed live locales may be declared; until one ships,
  // the set is the self-referencing default language + x-default.
  const clean = path.startsWith('/') ? path : `/${path}`
  const pathPrefixed = locales.filter((l) => l.ready && !l.default && l.href !== '/')
  return [
    { hreflang: defaultLocale.code, href: clean },
    ...pathPrefixed.map((l) => ({
      hreflang: l.code,
      href: `${l.href}${clean === '/' ? '' : clean}`,
    })),
    { hreflang: 'x-default', href: clean },
  ]
}
